<template>
    <div class="range-slider">
        <div class="range-slider__value">
            <div class="range-slider__value-hidden">
                {{ formatNumber(props.max) }}
            </div>

            <div
                v-if="props.settings.showMinMaxValues"
                class="range-slider__value-min"
            >
                {{ formatNumber(min) }}
            </div>

            <div class="range-slider__value-current">
                {{ formatNumber(props.value) }}
            </div>

            <div
                v-if="props.settings.showMinMaxValues"
                class="range-slider__value-max"
            >
                {{ formatNumber(max) }}
            </div>
        </div>

        <div ref="slider" class="range-slider__slider">
            <div
                :style="{
                    left: (value - min) / (max - min) * 100 + '%',
                }"
                class="range-slider__handle-wrapper"
            >
                <div
                    ref="handle"
                    class="range-slider__handle"
                    tabindex="0"
                ></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const emit = defineEmits(['updateValue']);
const props = defineProps({
    value: {
        type: Number,
        default: 0,
    },

    min: {
        type: Number,
        default: 0,
    },

    max: {
        type: Number,
        default: 5000,
    },

    steps: {
        type: Number,
        default: 50,
    },

    settings: {
        type: Object,
        default: () => ({}),
    },
});

const slider = ref(null);
const handle = ref(null);

const updateValue = newValue => {
    emit('updateValue', newValue);
};

const handleSlider = () => {
    [slider.value, handle.value].forEach(element => {
        if (store.state.page.deviceType === 'desktop' || store.state.page.isPreview) {
            element.addEventListener('mousedown', event => {
                const sliderWidth = slider.value.offsetWidth;
                const sliderLeft = slider.value.getBoundingClientRect().left;

                const moveHandle = event => {
                    const left = Math.min(Math.max(event.clientX - sliderLeft, 0), sliderWidth);
                    const percentage = left / sliderWidth;
                    const newValue = Math.round(percentage * (props.max - props.min) / props.steps) * props.steps + props.min;

                    updateValue(newValue);
                };

                moveHandle(event);

                document.addEventListener('mousemove', moveHandle);

                document.addEventListener('mouseup', () => {
                    document.removeEventListener('mousemove', moveHandle);
                });
            });
        } else {
            element.addEventListener('touchstart', event => {
                const sliderWidth = slider.value.offsetWidth;
                const sliderLeft = slider.value.getBoundingClientRect().left;

                const moveHandle = event => {
                    const left = Math.min(Math.max(event.changedTouches[0].clientX - sliderLeft, 0), sliderWidth);
                    const percentage = left / sliderWidth;
                    const newValue = Math.round(percentage * (props.max - props.min) / props.steps) * props.steps + props.min;

                    updateValue(newValue);
                };

                element.addEventListener('touchmove', moveHandle);
            });
        }
    });

    handle.value.addEventListener('keydown', event => {
        const step = props.steps;
        const value = props.value;

        switch (event.key) {
            case 'ArrowLeft':
                updateValue(Math.max(value - step, props.min));
                break;

            case 'ArrowRight':
                updateValue(Math.min(value + step, props.max));
                break;
        }
    });
};

const formatNumber = value => {
    const useThousandSeparator = props.settings.showThousandsSeparator;
    const prefix = props.settings.prefix || '';
    const suffix = props.settings.suffix || '';

    if (useThousandSeparator) {
        const language = store.state.page.language;
        const countryCode = store.state.page.countryCode;
        const locales = language + '-' + countryCode.toUpperCase();

        value = new Intl.NumberFormat(locales).format(value);
    }

    return prefix + value + suffix;
};

onMounted(() => {
    handleSlider();
});
</script>

<style lang="scss">
@use 'zoe-atom-range-slider';
</style>
